import { useEffect, useState, useMemo, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { GripVertical, RotateCcw, Save, Activity } from "lucide-react";

const HEADSHOT_FALLBACK = "https://a.espncdn.com/i/headshots/nfl/players/full/0.png";
const STORAGE_KEY = "titans_depth_order_v1";

const slugId = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

// Injury status → visual chip. Kept small so it fits in the 12-13px header row.
const INJURY_CHIP = {
  Out:          { label: "OUT", cls: "bg-red-600 text-white" },
  IR:           { label: "IR",  cls: "bg-red-700 text-white" },
  Doubtful:     { label: "D",   cls: "bg-orange-500 text-white" },
  Questionable: { label: "Q",   cls: "bg-amber-400 text-black" },
  Probable:     { label: "P",   cls: "bg-emerald-500 text-white" },
  PUP:          { label: "PUP", cls: "bg-purple-600 text-white" },
  Suspended:    { label: "SUS", cls: "bg-slate-700 text-white" },
};

const POSITION_GROUPS = [
  {
    name: "Offense",
    accent: "bg-titans-blue",
    columns: [
      { code: "QB", label: "Quarterback", depth: 3 },
      { code: "RB", label: "Running Back", depth: 4 },
      { code: "WR", label: "Wide Receiver", depth: 6 },
      { code: "TE", label: "Tight End", depth: 4 },
      { code: "LT", label: "Left Tackle", depth: 2, alt: ["OT"] },
      { code: "LG", label: "Left Guard", depth: 2, alt: ["OG", "G"] },
      { code: "C", label: "Center", depth: 2 },
      { code: "RG", label: "Right Guard", depth: 2, alt: ["OG", "G"] },
      { code: "RT", label: "Right Tackle", depth: 2, alt: ["OT"] },
    ],
  },
  {
    name: "Defense",
    accent: "bg-titans-red",
    columns: [
      { code: "DE", label: "Defensive End", depth: 3 },
      { code: "DT", label: "Defensive Tackle", depth: 4, alt: ["NT"] },
      { code: "OLB", label: "Outside Linebacker", depth: 3, alt: ["LB"] },
      { code: "ILB", label: "Inside Linebacker", depth: 3, alt: ["LB", "MLB"] },
      { code: "CB", label: "Cornerback", depth: 5 },
      { code: "S", label: "Safety", depth: 4, alt: ["FS", "SS"] },
    ],
  },
  {
    name: "Special Teams",
    accent: "bg-white",
    accentText: "text-titans-navy",
    columns: [
      { code: "K", label: "Kicker", depth: 1, alt: ["PK"] },
      { code: "P", label: "Punter", depth: 1 },
      { code: "LS", label: "Long Snapper", depth: 1 },
    ],
  },
];

const RANK_LABEL = (i) => (i === 0 ? "STARTER" : i === 1 ? "2ND" : i === 2 ? "3RD" : `${i + 1}TH`);

function SnapShareTooltip({ snaps, opps }) {
  if (!snaps || snaps.length === 0) return null;
  return (
    <div
      className="absolute z-50 left-0 top-full mt-1.5 w-[220px] p-3 rounded-lg bg-[#0B1B2E] border border-titans-blue/40 shadow-xl pointer-events-none"
      role="tooltip"
      data-testid="snap-share-tooltip"
    >
      <div className="font-cond uppercase tracking-[0.2em] text-[9px] text-titans-blue mb-2 flex items-center gap-1">
        <Activity size={10} /> Last 4 · Snap Share
      </div>
      <div className="flex items-end justify-between gap-1 h-14">
        {snaps.slice(0, 4).map((s, i) => (
          <div key={i} className="flex-1 flex flex-col items-center gap-1">
            <div className="w-full flex items-end h-10">
              <div
                className="w-full rounded-t bg-gradient-to-t from-titans-blue to-[#4B92DB]"
                style={{ height: `${Math.max(4, s)}%` }}
              />
            </div>
            <div className="font-display text-white text-[10px] tabular-nums">{s}%</div>
            {opps && opps[i] && (
              <div className="font-cond uppercase text-[8px] text-titans-silver tracking-wider">{opps[i]}</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function PlayerCard({ player, rank, isOver, isDragging, dragHandlers, injury, stats }) {
  const [showTip, setShowTip] = useState(false);
  if (!player) {
    return (
      <div
        {...dragHandlers}
        className={`border border-dashed border-titans-silver/30 rounded-[14px] p-3 text-titans-silver/60 text-xs font-cond uppercase tracking-wider text-center ${
          isOver ? "bg-titans-blue/20 border-titans-blue" : ""
        }`}
      >
        — Drop here —
      </div>
    );
  }
  const isStarter = rank === 0;
  const cardCls = isStarter ? "starter-card" : "titans-card--flat";
  const chip = injury ? INJURY_CHIP[injury.status] : null;
  const hasSnapData = isStarter && stats?.snap_shares_last_4?.length > 0;
  return (
    <div
      {...dragHandlers}
      onMouseEnter={() => hasSnapData && setShowTip(true)}
      onMouseLeave={() => setShowTip(false)}
      className={`${cardCls} relative p-2.5 flex items-center gap-2.5 cursor-grab active:cursor-grabbing select-none transition-all duration-200 ${
        isDragging ? "opacity-40" : ""
      } ${isOver ? "ring-2 ring-titans-blue ring-offset-2 ring-offset-titans-void" : ""}`}
      data-testid={isStarter ? `starter-card-${slugId(player.fullName)}` : undefined}
    >
      <GripVertical size={14} className="text-slate-400/40 shrink-0" />
      <div className="relative h-12 w-12 bg-titans-elev shrink-0 overflow-hidden rounded-md">
        <img
          src={player.headshot || HEADSHOT_FALLBACK}
          alt={player.fullName}
          className="w-full h-full object-cover pointer-events-none"
          onError={(e) => { e.currentTarget.src = HEADSHOT_FALLBACK; }}
        />
        <span className="absolute bottom-0 left-0 right-0 bg-titans-navy/85 text-white font-display text-[10px] text-center leading-tight">
          #{player.jersey || "--"}
        </span>
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <div className="font-display text-[17px] leading-tight uppercase truncate text-[#0B1B2E]" title={player.fullName}>
            {player.fullName}
          </div>
          {chip && (
            <span
              className={`shrink-0 inline-flex items-center px-1.5 py-[1px] rounded-md font-cond font-bold text-[9px] tracking-[0.1em] leading-none ${chip.cls}`}
              title={injury.injury ? `${injury.status} — ${injury.injury}` : injury.status}
              data-testid={`injury-chip-${slugId(player.fullName)}`}
            >
              {chip.label}
            </span>
          )}
          {hasSnapData && (
            <Activity size={11} className="text-titans-blue shrink-0" aria-hidden="true" />
          )}
        </div>
        <div className="mt-0.5 flex items-center gap-2 flex-wrap">
          {isStarter ? (
            <span className="starter-pill">Starter</span>
          ) : (
            <span className="font-cond uppercase tracking-wider text-[11px] font-semibold text-[#4B5563]">
              {RANK_LABEL(rank)}
            </span>
          )}
          <span className="font-cond uppercase tracking-wider text-[11px] text-[#6B7280]">
            {player.position || "—"}
            {player.experience != null && ` · ${player.experience} yr`}
          </span>
        </div>
      </div>
      {showTip && hasSnapData && (
        <SnapShareTooltip snaps={stats.snap_shares_last_4} opps={stats.opponents_last_4} />
      )}
    </div>
  );
}

export default function DepthChart() {
  const [athletes, setAthletes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState({}); // { POS_CODE: [playerId, ...] }
  const [drag, setDrag] = useState(null); // { col, fromIdx, fromPlayerId }
  const [hover, setHover] = useState(null); // { col, idx }
  const [injuryMap, setInjuryMap] = useState({}); // slug → injury row
  const [statsMap, setStatsMap] = useState({});   // slug → player_stats row
  const savedRef = useRef(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/roster");
        setAthletes(data.athletes || []);
      } catch {
        toast.error("Failed to load roster");
      } finally {
        setLoading(false);
      }
    })();
    // Injuries — public, low-frequency change.
    api.get("/injuries").then(({ data }) => {
      const map = {};
      for (const inj of data.injuries || []) {
        if (inj.player_id) map[inj.player_id] = inj;
        if (inj.full_name) map[slugId(inj.full_name)] = inj;
      }
      setInjuryMap(map);
    }).catch(() => {});
    // Snap-share stats (starters only).
    api.get("/player-stats").then(({ data }) => {
      const map = {};
      for (const s of data.stats || []) {
        if (s.player_id) map[s.player_id] = s;
        if (s.full_name) map[slugId(s.full_name)] = s;
      }
      setStatsMap(map);
    }).catch(() => {});
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
      setOrder(saved);
    } catch (e) {
      console.error("DepthChart load saved order failed:", e);
    }
  }, []);

  useEffect(() => {
    if (!savedRef.current) { savedRef.current = true; return; }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(order));
  }, [order]);

  const byPosition = useMemo(() => {
    const map = {};
    for (const a of athletes) {
      const pos = (a.position || "").toUpperCase();
      if (!pos) continue;
      if (!map[pos]) map[pos] = [];
      map[pos].push(a);
    }
    for (const k of Object.keys(map)) {
      map[k].sort((a, b) => (b.experience || 0) - (a.experience || 0) || (parseInt(a.jersey) || 99) - (parseInt(b.jersey) || 99));
    }
    return map;
  }, [athletes]);

  const playersForColumn = (col) => {
    let pool = byPosition[col.code] || [];
    if (!pool.length && col.alt) {
      const merged = [];
      for (const alt of col.alt) {
        const list = byPosition[alt] || [];
        for (const p of list) if (!merged.find((x) => x.id === p.id)) merged.push(p);
      }
      pool = merged;
    }
    const baseIds = pool.map((p) => p.id);
    const custom = order[col.code];
    let finalIds;
    if (custom && custom.length) {
      const set = new Set(baseIds);
      finalIds = [...custom.filter((id) => set.has(id)), ...baseIds.filter((id) => !custom.includes(id))];
    } else {
      finalIds = baseIds;
    }
    // Show ALL players at this position, never truncate
    return finalIds.map((id) => pool.find((p) => p.id === id) || null);
  };

  // Compute "claimed" position codes (including alts) so we can find leftovers
  const claimedCodes = useMemo(() => {
    const set = new Set();
    for (const group of POSITION_GROUPS) {
      for (const col of group.columns) {
        set.add(col.code);
        for (const a of col.alt || []) set.add(a);
      }
    }
    return set;
  }, []);

  const leftovers = useMemo(() => {
    const arr = [];
    for (const [code, list] of Object.entries(byPosition)) {
      if (!claimedCodes.has(code)) {
        for (const p of list) arr.push(p);
      }
    }
    return arr;
  }, [byPosition, claimedCodes]);

  const onDragStart = (colCode, idx, playerId) => (e) => {
    setDrag({ col: colCode, fromIdx: idx, fromPlayerId: playerId });
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", playerId || ""); } catch (err) { console.warn("dataTransfer.setData failed:", err); }
  };

  const onDragOver = (colCode, idx) => (e) => {
    if (!drag || drag.col !== colCode) return; // only same column
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (hover?.col !== colCode || hover?.idx !== idx) setHover({ col: colCode, idx });
  };

  const onDrop = (colCode, toIdx, col) => (e) => {
    if (!drag || drag.col !== colCode) { setHover(null); setDrag(null); return; }
    e.preventDefault();
    const players = playersForColumn(col);
    const ids = players.map((p) => (p ? p.id : null));
    const fromId = drag.fromPlayerId;
    if (!fromId) { setHover(null); setDrag(null); return; }
    // Build new id order
    const filtered = ids.filter((id) => id !== fromId);
    const targetIdx = Math.min(toIdx, filtered.length);
    filtered.splice(targetIdx, 0, fromId);
    setOrder((o) => ({ ...o, [colCode]: filtered.filter(Boolean) }));
    setHover(null); setDrag(null);
  };

  const onDragEnd = () => { setDrag(null); setHover(null); };

  const resetAll = () => {
    setOrder({});
    localStorage.removeItem(STORAGE_KEY);
    toast.success("Depth chart reset to default");
  };

  const customCount = Object.values(order).filter((arr) => Array.isArray(arr) && arr.length).length;

  if (loading) {
    return <div data-testid="depth-chart-loading" className="text-titans-silver">Loading depth chart…</div>;
  }

  return (
    <div data-testid="depth-chart">
      <div className="mb-6 flex flex-wrap items-center gap-3 justify-between">
        <div className="flex gap-3 text-[10px] font-cond uppercase tracking-wider items-center">
          <span className="inline-flex items-center gap-1"><span className="w-3 h-3 bg-titans-blue rounded-sm" />Starter</span>
          <span className="inline-flex items-center gap-1"><span className="w-3 h-3 bg-white border border-[#D9E2EC] rounded-sm" />Bench</span>
          <span className="text-titans-silver ml-2 inline-flex items-center gap-1">
            <GripVertical size={12} /> Drag to reorder within a position
          </span>
        </div>
        <div className="flex gap-2 items-center">
          {customCount > 0 && (
            <span className="font-cond uppercase tracking-wider text-[10px] text-titans-blue inline-flex items-center gap-1">
              <Save size={12} /> {customCount} of your picks saved
            </span>
          )}
          <button
            type="button"
            onClick={resetAll}
            data-testid="depth-reset-btn"
            className="border border-[rgba(138,141,143,0.3)] text-titans-silver hover:text-white hover:border-white px-3 py-1.5 font-cond uppercase tracking-wider text-[10px] inline-flex items-center gap-1"
          >
            <RotateCcw size={12} /> Reset
          </button>
        </div>
      </div>

      {POSITION_GROUPS.map((group) => (
        <section key={group.name} className="mb-12" data-testid={`depth-group-${group.name.toLowerCase().replace(/\s+/g, "-")}`}>
          <div className="flex items-center gap-3 mb-5">
            <div className={`${group.accent} ${group.accentText || "text-white"} font-display text-xl uppercase tracking-wider px-4 py-1`}>
              {group.name}
            </div>
            <div className="h-px bg-titans-silver/20 flex-1" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {group.columns.map((col) => {
              const players = playersForColumn(col);
              // Always show at least col.depth slots so empty drop zones appear;
              // but if there are more players than col.depth, show them all.
              const slotCount = Math.max(col.depth, players.length);
              return (
                <div
                  key={col.code}
                  className="space-y-2.5"
                  data-testid={`depth-col-${col.code}`}
                  onDragEnd={onDragEnd}
                >
                  <div className="flex items-baseline justify-between border-b border-titans-blue/40 pb-1 mb-2">
                    <span className="font-display text-lg uppercase text-white">{col.code}</span>
                    <span className="font-cond uppercase tracking-wider text-[10px] text-titans-silver">{col.label}</span>
                  </div>
                  {Array.from({ length: slotCount }).map((_, idx) => {
                    const p = players[idx];
                    const slotKey = `${col.code}-slot-${idx}`;
                    const dragHandlers = p
                      ? {
                          draggable: true,
                          onDragStart: onDragStart(col.code, idx, p.id),
                          onDragOver: onDragOver(col.code, idx),
                          onDrop: onDrop(col.code, idx, col),
                          onDragEnd: onDragEnd,
                        }
                      : {
                          onDragOver: onDragOver(col.code, idx),
                          onDrop: onDrop(col.code, idx, col),
                        };
                    const isOver = hover?.col === col.code && hover?.idx === idx;
                    const isDragging = drag?.col === col.code && drag?.fromIdx === idx;
                    const nameSlug = p ? slugId(p.fullName) : null;
                    return (
                      <PlayerCard
                        key={slotKey}
                        player={p}
                        rank={idx}
                        isOver={isOver}
                        isDragging={isDragging}
                        dragHandlers={dragHandlers}
                        injury={nameSlug ? injuryMap[nameSlug] : null}
                        stats={nameSlug ? statsMap[nameSlug] : null}
                      />
                    );
                  })}
                </div>
              );
            })}
          </div>
        </section>
      ))}

      {leftovers.length > 0 && (
        <section className="mb-12" data-testid="depth-group-reserves">
          <div className="flex items-center gap-3 mb-5">
            <div className="bg-titans-silver text-titans-navy font-display text-xl uppercase tracking-wider px-4 py-1">
              Reserves & Specialists
            </div>
            <div className="h-px bg-titans-silver/20 flex-1" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {leftovers.map((p) => (
              <div key={p.id} className="titans-card--flat p-2.5 flex items-center gap-2.5">
                <div className="relative h-12 w-12 bg-titans-elev shrink-0 overflow-hidden rounded-md">
                  <img
                    src={p.headshot || HEADSHOT_FALLBACK}
                    alt={p.fullName}
                    className="w-full h-full object-cover"
                    onError={(e) => { e.currentTarget.src = HEADSHOT_FALLBACK; }}
                  />
                  <span className="absolute bottom-0 left-0 right-0 bg-titans-navy/85 text-white font-display text-[10px] text-center leading-tight">
                    #{p.jersey || "--"}
                  </span>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-display text-[17px] leading-tight uppercase truncate text-[#0B1B2E]">{p.fullName}</div>
                  <div className="font-cond uppercase tracking-wider text-[11px] text-[#6B7280]">
                    {p.position || "—"}{p.experience != null ? ` · ${p.experience} yr` : ""}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
